import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { BearDecimalInput } from "@/lib/form/ui";
import { Loader2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  RiskCropProfile,
  useUpdateProfile,
  UpdateCustomerCropProfileValidation,
} from "@/lib/api/risk/profiles";

interface EditParentProfileDialogProps {
  isOpen: boolean;
  onClose: () => void;
  profile: RiskCropProfile;
  onSuccess?: () => void;
}

const formSchema = z.object({
  acreage: z.string(),
  projected_yield: z.string(),
  actual_yield: z.string(),
  cost_per_acre: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

export function EditParentProfileDialog({
  isOpen,
  onClose,
  profile,
  onSuccess,
}: EditParentProfileDialogProps) {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const updateProfile = useUpdateProfile();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      acreage: profile.acreage?.toString() || "",
      projected_yield: profile.projected_yield?.toString() || "",
      actual_yield: profile.actual_yield?.toString() || "",
      cost_per_acre: profile.cost_per_acre?.toString() || "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      const data: z.infer<typeof UpdateCustomerCropProfileValidation> = {
        is_enabled: profile.is_enabled,
        commodity_name: profile.commodity_name as any,
        crop_year: profile.crop_year,
        acreage: values.acreage,
        projected_yield: values.projected_yield,
        actual_yield: values.actual_yield,
        cost_per_acre: values.cost_per_acre,
      };

      const result = await updateProfile(profile.id, data);
      if (result.success) {
        toast({ title: "Profile updated successfully" });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast({
          title: "Failed to update profile",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "An error occurred while updating the profile",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] lg:max-w-[700px] lg:min-w-[700px]">
        <DialogHeader>
          <DialogTitle>Edit Parent Profile</DialogTitle>
          <DialogDescription>
            Update the values for {profile.commodity_name} ({profile.crop_year})
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="acreage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Acreage</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Acreage"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>
                    Total planted acres for this crop
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="projected_yield"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Projected Yield</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Projected Yield"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Expected yield per acre</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="actual_yield"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Actual Yield</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Actual Yield"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Realized yield per acre</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="cost_per_acre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cost Per Acre</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Cost Per Acre"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Cost per acre for this crop</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                Update Profile
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
